import { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { formatTL } from "../lib/api";
import { describePart, LABOR_DESCRIPTION, INCLUDED_SERVICES, WARRANTY } from "../lib/partDictionary";
import { FileDown, MessageSquare, X, Phone, ChevronDown, ChevronUp, ShieldCheck, CheckCircle2, Wrench, Cpu } from "lucide-react";

export default function QuoteCustomerView({ quote, settings, onClose, onDownload, onShare }) {
  const [expanded, setExpanded] = useState({}); // { idx: true }
  if (!quote) return null;
  const footer = (quote.footer_note && quote.footer_note.trim()) || settings?.quote_footer_note || "";
  const company = settings?.company_name || "ServisPlus";
  const companyPhone = settings?.company_phone || "";
  const hasDevice = quote.device_brand_name || quote.device_model || quote.device_serial || quote.device_type;

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-2xl p-0 overflow-hidden bg-white max-h-[95vh]"
        data-testid="customer-quote-view"
      >
        <div className="relative flex flex-col max-h-[95vh]">
          {/* Header */}
          <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <img src="/brand/logo.png" alt="logo" className="h-12 w-auto object-contain bg-white rounded p-1.5" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-300">Fiyat Teklifi</div>
                <div className="font-display font-bold text-xl">{company}</div>
                {companyPhone && <div className="text-xs text-slate-400 mt-0.5">{companyPhone}</div>}
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-2 -mr-1" data-testid="close-view">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
            {/* Müşteri + Teklif No */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Sayın</div>
                <div className="font-display font-bold text-2xl text-slate-900">{quote.customer_name}</div>
                {quote.customer_phone && <div className="text-sm text-slate-500 mt-0.5">{quote.customer_phone}</div>}
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Teklif No</div>
                <div className="font-mono font-bold text-blue-700">{quote.quote_number}</div>
              </div>
            </div>

            {/* Cihaz Bilgileri */}
            {hasDevice && (
              <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="h-4 w-4 text-blue-700" />
                  <div className="text-[11px] uppercase tracking-wider font-bold text-blue-700">Cihaz Bilgileri</div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {quote.device_brand_name && <div><span className="text-slate-500">Marka:</span> <span className="font-semibold text-slate-900">{quote.device_brand_name}</span></div>}
                  {quote.device_type && <div><span className="text-slate-500">Tip:</span> <span className="font-semibold text-slate-900">{quote.device_type}</span></div>}
                  {quote.device_model && <div><span className="text-slate-500">Model:</span> <span className="font-semibold text-slate-900">{quote.device_model}</span></div>}
                  {quote.device_serial && <div><span className="text-slate-500">Seri No:</span> <span className="font-mono font-semibold text-slate-900">{quote.device_serial}</span></div>}
                </div>
              </div>
            )}

            {/* Arıza */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wrench className="h-4 w-4 text-slate-600" />
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600">Arıza / İş Tanımı</div>
              </div>
              <div className="text-base text-slate-900">{quote.fault_description}</div>
            </div>

            {/* Kalemler */}
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">İşlem Kalemleri</div>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                {Number(quote.labor_fee) > 0 && (
                  <div>
                    <button
                      onClick={() => toggle("labor")}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                      data-testid="exp-labor"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-base">İşçilik Ücreti</div>
                        <div className="text-xs text-slate-500 mt-0.5">Teşhis · Montaj · Kalibrasyon · Test</div>
                      </div>
                      <div className="font-mono font-bold text-lg text-slate-900 ml-3 shrink-0">{formatTL(quote.labor_fee)}</div>
                      {expanded.labor ? <ChevronUp className="h-4 w-4 text-slate-400 ml-2" /> : <ChevronDown className="h-4 w-4 text-slate-400 ml-2" />}
                    </button>
                    {expanded.labor && (
                      <div className="px-4 pb-3 -mt-1 text-sm text-slate-600 leading-relaxed bg-slate-50/50">
                        {LABOR_DESCRIPTION}
                      </div>
                    )}
                  </div>
                )}
                {(quote.parts || []).map((p, i) => {
                  const desc = describePart(p.name);
                  const isOpen = expanded[i];
                  return (
                    <div key={i}>
                      <button
                        onClick={() => toggle(i)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                        data-testid={`exp-part-${i}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 text-base">{p.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{desc}</div>
                        </div>
                        <div className="font-mono font-bold text-lg text-slate-900 ml-3 shrink-0">{formatTL(p.amount)}</div>
                        {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400 ml-2" /> : <ChevronDown className="h-4 w-4 text-slate-400 ml-2" />}
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-3 -mt-1 text-sm text-slate-600 leading-relaxed bg-slate-50/50">
                          {desc}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* DAHİL Hizmetler */}
            <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-2.5">
                Bu Fiyata Dahil Olan Hizmetler
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-3">
                {INCLUDED_SERVICES.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-emerald-900">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Garanti */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-900 text-white rounded-xl p-4 flex items-center gap-3">
                <ShieldCheck className="h-7 w-7 text-emerald-400 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Parça</div>
                  <div className="font-bold text-base leading-tight">{WARRANTY.parts}</div>
                </div>
              </div>
              <div className="bg-slate-900 text-white rounded-xl p-4 flex items-center gap-3">
                <ShieldCheck className="h-7 w-7 text-emerald-400 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">İşçilik</div>
                  <div className="font-bold text-base leading-tight">{WARRANTY.labor}</div>
                </div>
              </div>
            </div>

            {/* Toplam */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white rounded-2xl p-6 flex items-end justify-between gap-3 shadow-lg">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-slate-400">Genel Toplam</div>
                <div className="text-[11px] text-slate-500 mt-1">Garantili Servis Paketi</div>
              </div>
              <div className="font-mono font-display font-bold text-4xl sm:text-5xl leading-none">
                {formatTL(quote.target_total)}
              </div>
            </div>

            {/* İndirim notu */}
            {footer && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
                <Phone className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900 leading-relaxed">{footer}</div>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="border-t border-slate-200 px-6 py-3 grid grid-cols-2 gap-3 bg-slate-50 shrink-0">
            <Button variant="outline" size="lg" onClick={onDownload} data-testid="view-download">
              <FileDown className="h-4 w-4 mr-2" /> PDF İndir
            </Button>
            <Button size="lg" onClick={onShare} data-testid="view-share">
              <MessageSquare className="h-4 w-4 mr-2" /> WhatsApp Gönder
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
